import { type FC, useEffect, useState } from "react";

import type { ListSchemaTelemetryOptions } from "../../types/schemaTelemetry";

import { useSchemaTelemetry } from "../../hooks/useSchemaTelemetry";
import { formatRelativeTime } from "../../utils/time";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { ContentCard } from "../common/ContentCard";
import { DataTable } from "../common/DataTable";
import { FormInput, FormSelect } from "../common/forms";
import { Pagination } from "../common/Pagination";
import PanelSection from "../common/PanelSection";
import { StatusBadge } from "../common/StatusBadge";
import { SchemaTelemetryDetailsModal } from "./SchemaTelemetryDetailsModal";

const VALIDATION_STATUS_OPTIONS = [
  { label: "All Statuses", value: "" },
  { label: "Success", value: "success" },
  { label: "Invalid", value: "invalid" },
  { label: "Error", value: "error" },
];

function parseValidationStatus(
  value: string,
): ListSchemaTelemetryOptions["validationStatus"] {
  switch (value) {
    case "error":
    case "invalid":
    case "success": {
      return value;
    }
    default: {
      return undefined;
    }
  }
}

const SchemaTelemetryRow: FC<{
  onSelect: (id: number) => void;
  row: typeof useSchemaTelemetry extends () => infer R
    ? R extends { data: { rows: infer T } }
      ? T extends (infer U)[]
        ? U
        : never
      : never
    : never;
}> = ({ onSelect, row }) => (
  <DataTable.Row onClick={() => onSelect(row.id)} className="cursor-pointer">
    <DataTable.Cell className="font-mono text-xs">{row.id}</DataTable.Cell>
    <DataTable.Cell className="text-xs text-gray-500">
      {formatRelativeTime(new Date(row.timestamp).getTime())}
    </DataTable.Cell>
    <DataTable.Cell className="text-sm font-medium">
      {row.schemaName ?? "-"}
    </DataTable.Cell>
    <DataTable.Cell className="text-sm">{row.provider ?? "-"}</DataTable.Cell>
    <DataTable.Cell
      className="max-w-[200px] truncate font-mono text-xs text-gray-500"
      title={row.callsite}
    >
      {row.callsite}
    </DataTable.Cell>
    <DataTable.Cell>
      <StatusBadge status={row.validationStatus} size="sm" />
    </DataTable.Cell>
    <DataTable.Cell className="text-right">
      <Button
        variant="ghost"
        size="sm"
        onClick={(event) => {
          event.stopPropagation();
          onSelect(row.id);
        }}
      >
        View
      </Button>
    </DataTable.Cell>
  </DataTable.Row>
);

export const SchemaTelemetryPanel: FC = () => {
  const {
    loading,
    error,
    data,
    fetchList,
    selectedId,
    setSelectedId,
    detail,
    detailLoading,
    handleExport,
  } = useSchemaTelemetry();

  const [filters, setFilters] = useState<ListSchemaTelemetryOptions>({
    callsite: "",
    page: 1,
    pageSize: 50,
    provider: "",
    schemaName: "",
    search: "",
    validationStatus: undefined,
  });

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev) => ({ ...prev, page: 1, search: debouncedSearch }));
    }, 300);
    return () => clearTimeout(handler);
  }, [debouncedSearch]);

  const updateFilter = <K extends keyof ListSchemaTelemetryOptions>(
    key: K,
    value: ListSchemaTelemetryOptions[K],
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  useEffect(() => {
    void fetchList(filters);
  }, [filters, fetchList]);

  return (
    <div className="space-y-4">
      {/* Header with Export Action */}
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-[11px] font-bold uppercase tracking-widest text-gray-900 dark:text-white">
          Schema Telemetry
        </h2>
        <Button
          variant="outline"
          size="sm"
          disabled={data.rows.length === 0}
          onClick={() => void handleExport(data.rows.map((r) => r.id))}
        >
          Export Page
        </Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {/* Filters Panel */}
      <PanelSection>
        <div className="grid gap-3 p-3 md:grid-cols-2 lg:grid-cols-5">
          <FormInput
            placeholder="Search snippets..."
            value={debouncedSearch}
            onChange={(e) => setDebouncedSearch(e.target.value)}
            className="w-full"
          />
          <FormInput
            placeholder="Schema Name"
            value={filters.schemaName ?? ""}
            onChange={(e) => updateFilter("schemaName", e.target.value)}
            className="w-full"
          />
          <FormInput
            placeholder="Provider"
            value={filters.provider ?? ""}
            onChange={(e) => updateFilter("provider", e.target.value)}
            className="w-full"
          />
          <FormInput
            placeholder="Callsite"
            value={filters.callsite ?? ""}
            onChange={(e) => updateFilter("callsite", e.target.value)}
            className="w-full"
          />
          <FormSelect
            options={VALIDATION_STATUS_OPTIONS}
            value={filters.validationStatus ?? ""}
            onChange={(e) => {
              updateFilter(
                "validationStatus",
                parseValidationStatus(e.target.value),
              );
            }}
          />
        </div>
      </PanelSection>

      {/* Results Table */}
      <ContentCard
        padding="none"
        className="overflow-hidden dark:bg-gray-800/50"
      >
        <DataTable>
          <DataTable.Header>
            <DataTable.Row>
              <DataTable.HeaderCell className="w-20">ID</DataTable.HeaderCell>
              <DataTable.HeaderCell>Timestamp</DataTable.HeaderCell>
              <DataTable.HeaderCell>Schema</DataTable.HeaderCell>
              <DataTable.HeaderCell>Provider</DataTable.HeaderCell>
              <DataTable.HeaderCell>Callsite</DataTable.HeaderCell>
              <DataTable.HeaderCell>Status</DataTable.HeaderCell>
              <DataTable.HeaderCell className="text-right">
                Actions
              </DataTable.HeaderCell>
            </DataTable.Row>
          </DataTable.Header>
          <DataTable.Body>
            {loading && data.rows.length === 0 && (
              <DataTable.Row>
                <DataTable.Cell
                  colSpan={7}
                  className="py-8 text-center text-gray-500"
                >
                  Loading...
                </DataTable.Cell>
              </DataTable.Row>
            )}
            {!loading && data.rows.length === 0 && (
              <DataTable.Row>
                <DataTable.Cell
                  colSpan={7}
                  className="py-8 text-center text-gray-500"
                >
                  No records found.
                </DataTable.Cell>
              </DataTable.Row>
            )}
            {data.rows.length > 0 &&
              data.rows.map((row) => (
                <SchemaTelemetryRow
                  key={row.id}
                  row={row}
                  onSelect={setSelectedId}
                />
              ))}
          </DataTable.Body>
        </DataTable>
      </ContentCard>

      <Pagination
        page={filters.page ?? 1}
        pageSize={filters.pageSize ?? 50}
        total={data.total}
        onPageChange={(p) => setFilters((prev) => ({ ...prev, page: p }))}
      />

      <SchemaTelemetryDetailsModal
        record={detail}
        isOpen={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        loading={detailLoading}
      />
    </div>
  );
};
